<template>
  <v-card ref="card" class="om">
    <div class="om__head">
      <div class="om__heading">
        <span class="om__title">Bestellkarte</span>
        <span v-if="meta" class="om__hint">Bestellungen je Postleitzahl · Stand {{ updated }}</span>
        <span v-else class="om__hint">Noch keine Kartendaten, sie entstehen nachts durch buildOrderMap.</span>
      </div>
      <div v-if="data" class="om__controls">
        <div class="om__chips">
          <button v-for="s in shopOptions" :key="s.key" type="button" class="om__chip" :class="{ 'om__chip--active': shop === s.key }" @click="shop = s.key">{{ s.label }}</button>
        </div>
        <div class="om__chips om__chips--sep">
          <button v-for="y in yearOptions" :key="y.key" type="button" class="om__chip" :class="{ 'om__chip--active': year === y.key }" @click="year = y.key">{{ y.label }}</button>
        </div>
        <div class="om__chips om__chips--sep">
          <button type="button" class="om__chip om__chip--icon" :class="{ 'om__chip--active': view === 'map' }" title="Karte" @click="view = 'map'"><v-icon x-small>mdi-map-outline</v-icon></button>
          <button type="button" class="om__chip om__chip--icon" :class="{ 'om__chip--active': view === 'table' }" title="Tabelle" @click="view = 'table'"><v-icon x-small>mdi-table</v-icon></button>
        </div>
      </div>
    </div>

    <div v-if="error" class="om__empty">{{ error }}</div>
    <div v-else-if="meta && !data" class="om__empty"><v-progress-circular indeterminate size="18" width="2" class="mr-2" />Kartendaten werden geladen …</div>

    <template v-if="data">
      <!-- Kennzahlen zum aktuellen Filter -->
      <div class="om__kpis">
        <div class="kpi"><div class="kpi__label">Bestellungen auf der Karte</div><div class="kpi__value">{{ fmtInt(filtered.orders) }}</div></div>
        <div class="kpi"><div class="kpi__label">Postleitzahlen</div><div class="kpi__value">{{ fmtInt(filtered.points.length) }}</div></div>
        <div class="kpi"><div class="kpi__label">Länder</div><div class="kpi__value">{{ filtered.countries.length }}</div></div>
        <div class="kpi" :title="unmappedTitle"><div class="kpi__label">Ohne Kartenpunkt</div><div class="kpi__value kpi__value--muted">{{ fmtInt(data.totals.unmapped) }}</div><div class="kpi__sub">PLZ unbekannt, alle Jahre</div></div>
      </div>

      <!-- Länder: Klick zoomt auf das Land -->
      <div class="om__countries">
        <button v-for="c in filtered.countries" :key="c.cc" type="button" class="om__country" :class="{ 'om__country--active': focusCc === c.cc }" @click="zoomToCountry(c.cc)">
          <b>{{ c.cc }}</b><span>{{ fmtInt(c.orders) }}</span>
        </button>
        <button type="button" class="om__country" title="Europa zeigen" @click="resetView"><v-icon x-small>mdi-earth</v-icon></button>
      </div>

      <div v-show="view === 'map'" class="om__mapWrap">
        <div ref="map" class="om__map" />
        <div class="om__legend">
          <span v-for="b in buckets" :key="b.label" class="om__legendItem"><i :style="{ background: b.color, width: b.size + 'px', height: b.size + 'px' }" />{{ b.label }}</span>
          <span class="om__legendNote">Kreisgröße und Farbe nach Bestellungen je Postleitzahl</span>
        </div>
      </div>

      <div v-if="view === 'table'" class="om__tableWrap">
        <table class="om-table">
          <thead><tr><th>Land</th><th>PLZ</th><th>Ort</th><th class="num">Bestellungen</th></tr></thead>
          <tbody>
            <tr v-for="p in tableRows" :key="p.cc + p.pc"><td>{{ p.cc }}</td><td>{{ p.pc }}<span v-if="p.approx" class="om-muted" title="Postleitzahl nur über den Anfang zugeordnet"> ~</span></td><td>{{ p.place }}</td><td class="num">{{ fmtInt(p.n) }}</td></tr>
          </tbody>
        </table>
        <div class="om__hint mt-2">Die {{ tableRows.length }} Postleitzahlen mit den meisten Bestellungen im aktuellen Filter.</div>
      </div>
    </template>
  </v-card>
</template>

<script>
// Bestellkarte: Kartendatei aus Storage (deliveryStats/orderMap.url, nachts von buildOrderMap geschrieben).
// Nur Land, PLZ, Ort und Anzahlen, keine Kundendaten. Leaflet mit Canvas-Renderer, ~23.500 Punkte.
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../fb";

// Blaue Ordinal-Stufen (dataviz-Palette, validiert: monotone Helligkeit, hellste Stufe ≥ 2:1 zur Fläche).
// Grenzen nach der echten Verteilung je PLZ (Median 2, p75 7, p90 16).
const BUCKETS = [
  { min: 16, label: "16+", color: "#0d366b", size: 14 },
  { min: 6, label: "6–15", color: "#1c5cab", size: 11 },
  { min: 2, label: "2–5", color: "#3987e5", size: 8 },
  { min: 1, label: "1", color: "#86b6ef", size: 6 },
];
const EUROPE = { center: [50.2, 7.5], zoom: 5 };
// Kreise in Pixeln, mit dem Zoom wachsend: auf Europa-Zoom klein (sonst verschmilzt Deutschland zu einer Fläche),
// ab Stadt-Zoom groß genug zum Hovern; Rand erst ab Zoom 7
const zoomScale = (z) => Math.max(0.42, Math.min(1.4, 0.42 + (z - 5) * 0.3));
const radiusFor = (n, z) => (1.6 + Math.sqrt(n) * 1.05) * zoomScale(z);
const strokeFor = (z) => (z < 7 ? 0 : 0.8);
const colorFor = (n) => (BUCKETS.find((b) => n >= b.min) || BUCKETS[BUCKETS.length - 1]).color;

export default {
  name: "OrderMapCard",
  data: () => ({
    meta: null,
    data: null,
    error: "",
    shop: "all",
    year: "all",
    view: "map",
    focusCc: null,
    loadedUrl: null,
    visible: false,
  }),
  computed: {
    updated() {
      return this.meta && this.meta.updatedAt ? new Date(this.meta.updatedAt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
    },
    buckets: () => BUCKETS,
    shopOptions() {
      return [{ key: "all", label: "Alle Shops" }, ...((this.data && this.data.shops) || []).filter((s) => s !== "?").map((s) => ({ key: s, label: s }))];
    },
    yearOptions() {
      return [{ key: "all", label: "Alle Jahre" }, ...((this.data && this.data.years) || []).map((y) => ({ key: y, label: String(y) }))];
    },
    /** Punkte zum aktuellen Shop-/Jahr-Filter: { lat, lon, cc, pc, place, n, approx } */
    filtered() {
      const out = { points: [], orders: 0, countries: [] };
      if (!this.data) return out;
      const byCc = {};
      for (const p of this.data.points) {
        let n = 0;
        if (this.shop === "all" && this.year === "all") n = p[5];
        else for (const [s, y, c] of p[7]) if ((this.shop === "all" || s === this.shop) && (this.year === "all" || y === this.year)) n += c;
        if (!n) continue;
        out.points.push({ lat: p[0], lon: p[1], cc: p[2], pc: p[3], place: p[4], n, approx: !!p[6] });
        out.orders += n;
        byCc[p[2]] = (byCc[p[2]] || 0) + n;
      }
      out.countries = Object.entries(byCc).map(([cc, orders]) => ({ cc, orders })).sort((a, b) => b.orders - a.orders);
      return out;
    },
    tableRows() {
      return this.filtered.points.slice().sort((a, b) => b.n - a.n).slice(0, 100);
    },
    unmappedTitle() {
      const u = (this.data && this.data.unmappedByCountry) || {};
      return Object.entries(u).sort((a, b) => b[1] - a[1]).map(([cc, n]) => `${cc} ${n}`).join(" · ");
    },
  },
  watch: {
    filtered() {
      this.drawPoints();
    },
    view(v) {
      if (v === "map") this.$nextTick(() => this.map && this.map.invalidateSize());
    },
  },
  created() {
    // Leaflet-Objekte bewusst nicht reaktiv
    this.map = null;
    this.layer = null;
    this.renderer = null;
  },
  mounted() {
    this.unsub = onSnapshot(doc(db, "deliveryStats", "orderMap"), (snap) => {
      this.meta = snap.exists() ? snap.data() : null;
      this.maybeLoad();
    }, (e) => { this.error = `Kartendaten nicht lesbar: ${e.message}`; });
    // Karte erst aufbauen, wenn die Karte in Sicht kommt (Statistik-Seite ist lang)
    this.io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this.visible = true;
        this.io.disconnect();
        this.maybeLoad();
      }
    }, { rootMargin: "200px" });
    this.io.observe(this.$refs.card.$el);
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
    if (this.io) this.io.disconnect();
    if (this.map) this.map.remove();
  },
  methods: {
    async maybeLoad() {
      if (!this.visible || !this.meta || !this.meta.url || this.meta.url === this.loadedUrl && this.data && this.data.updatedAt === this.meta.updatedAt) return;
      try {
        const res = await fetch(this.meta.url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.data = await res.json();
        this.loadedUrl = this.meta.url;
        this.error = "";
        this.$nextTick(() => this.initMap());
      } catch (e) {
        this.error = `Kartendatei nicht ladbar: ${e.message}`;
      }
    },
    initMap() {
      if (this.map || !this.$refs.map) {
        this.drawPoints();
        return;
      }
      this.renderer = L.canvas({ padding: 0.5 });
      this.map = L.map(this.$refs.map, { preferCanvas: true, renderer: this.renderer, zoomSnap: 0.5, worldCopyJump: true }).setView(EUROPE.center, EUROPE.zoom);
      this.map.on("zoomend", () => this.restyle());
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-Mitwirkende',
      }).addTo(this.map);
      this.drawPoints();
    },
    drawPoints() {
      if (!this.map) return;
      if (this.layer) this.layer.remove();
      const layer = L.layerGroup();
      const z = this.map.getZoom();
      // kleine zuerst, große oben drauf
      const pts = this.filtered.points.slice().sort((a, b) => a.n - b.n);
      for (const p of pts) {
        L.circleMarker([p.lat, p.lon], {
          renderer: this.renderer,
          radius: radiusFor(p.n, z),
          fillColor: colorFor(p.n),
          fillOpacity: 0.8,
          color: "#ffffff",
          weight: strokeFor(z),
          n: p.n,
        })
          .bindTooltip(() => `<b>${p.pc}${p.approx ? " ~" : ""} ${this.escape(p.place)}</b> · ${p.cc}<br>${this.fmtInt(p.n)} Bestellung${p.n === 1 ? "" : "en"}`, { direction: "top", offset: [0, -4], className: "om-tip" })
          .addTo(layer);
      }
      layer.addTo(this.map);
      this.layer = layer;
    },
    /** Kreisgröße und Rand an den Zoom anpassen (Canvas zeichnet einmal neu, ~50 ms) */
    restyle() {
      if (!this.layer || !this.map) return;
      const z = this.map.getZoom();
      const weight = strokeFor(z);
      this.layer.eachLayer((l) => {
        l.setRadius(radiusFor(l.options.n, z));
        if (l.options.weight !== weight) l.setStyle({ weight });
      });
    },
    zoomToCountry(cc) {
      if (!this.map) return;
      const pts = this.filtered.points.filter((p) => p.cc === cc);
      if (!pts.length) return;
      // robuste Grenzen: Ausreißer (Überseegebiete, Tippfehler) über 2/98-Perzentile abschneiden
      const q = (arr, f) => arr.slice().sort((a, b) => a - b)[Math.min(arr.length - 1, Math.max(0, Math.floor(f * (arr.length - 1))))];
      const lats = pts.map((p) => p.lat);
      const lons = pts.map((p) => p.lon);
      this.map.fitBounds([[q(lats, 0.02), q(lons, 0.02)], [q(lats, 0.98), q(lons, 0.98)]], { padding: [24, 24], maxZoom: 9 });
      this.focusCc = cc;
    },
    resetView() {
      if (this.map) this.map.setView(EUROPE.center, EUROPE.zoom);
      this.focusCc = null;
    },
    fmtInt(n) {
      return Number(n || 0).toLocaleString("de-DE");
    },
    escape(s) {
      return String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    },
  },
};
</script>

<style lang="scss" scoped>
$ink: rgba(0, 0, 0, 0.87);
$muted: rgba(0, 0, 0, 0.5);
$line: rgba(0, 0, 0, 0.07);

.om {
  padding: 18px 20px 16px;
  &__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  &__heading { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
  &__title { font-weight: 700; font-size: 15px; }
  &__hint { font-size: 12px; color: $muted; }
  &__controls { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  &__chips { display: flex; gap: 6px; flex-wrap: wrap; &--sep { padding-left: 10px; border-left: 1px solid $line; } }
  &__chip {
    border: 1px solid rgba(0, 0, 0, 0.14); border-radius: 999px; padding: 4px 10px; font-size: 12px; background: #fff; cursor: pointer; line-height: 1.2;
    &--active { background: #282828; border-color: #282828; color: #fff; .v-icon { color: #fff; } }
    &--icon { padding: 4px 7px; }
  }
  &__empty { padding: 24px 0 8px; font-size: 13px; color: $muted; display: flex; align-items: center; }
  &__kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
  &__countries { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 14px; }
  &__country {
    display: inline-flex; align-items: center; gap: 6px; border: 1px solid $line; border-radius: 8px; padding: 4px 9px; background: #fff; font-size: 12px; cursor: pointer;
    b { color: $ink; }
    span { color: $muted; font-variant-numeric: tabular-nums; }
    &:hover { border-color: rgba(0, 0, 0, 0.2); }
    &--active { border-color: #282828; box-shadow: inset 0 0 0 1px #282828; }
  }
  &__mapWrap { margin-top: 12px; }
  &__map {
    height: 560px; border-radius: 12px; overflow: hidden; background: #f2f2f0; z-index: 0;
    /* Grundkarte entsättigt, damit die blauen Punkte tragen (OSM-Kacheln bleiben unverändert geladen) */
    :deep(.leaflet-tile-pane) { filter: grayscale(1) contrast(0.92) brightness(1.06); }
  }
  &__legend { display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: center; margin-top: 10px; font-size: 11px; color: $muted; }
  &__legendItem { display: inline-flex; align-items: center; gap: 6px; i { display: inline-block; border-radius: 50%; box-shadow: 0 0 0 1px #fff; } }
  &__legendNote { margin-left: auto; }
  &__tableWrap { margin-top: 14px; max-height: 560px; overflow-y: auto; }
}
.kpi {
  background: #f7f7f9; border-radius: 12px; padding: 12px 14px;
  &__label { font-size: 12px; color: $muted; }
  &__value { font-size: 26px; font-weight: 700; line-height: 1.15; margin-top: 2px; font-variant-numeric: tabular-nums; color: $ink; &--muted { color: $muted; } }
  &__sub { font-size: 11px; color: $muted; margin-top: 2px; }
}
.om-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: left; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: $muted; padding: 6px 8px; border-bottom: 1px solid rgba(0, 0, 0, 0.08); position: sticky; top: 0; background: #fff; }
  td { padding: 6px 8px; border-bottom: 1px solid rgba(0, 0, 0, 0.05); }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
}
.om-muted { color: $muted; }
@media (max-width: 760px) {
  .om__kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .om__map { height: 420px; }
}
</style>

<style lang="scss">
/* Leaflet-Tooltip im TaskDash-Ton (nicht scoped: Tooltip hängt am Kartencontainer) */
.leaflet-tooltip.om-tip {
  background: #282828; color: #fff; border: 0; border-radius: 8px; padding: 6px 9px; font-size: 12px; line-height: 1.35; box-shadow: 0 6px 18px rgba(16, 24, 40, 0.25);
  &.leaflet-tooltip-top::before { border-top-color: #282828; }
}
</style>
